import { useCallback } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useDebouncedCallback } from 'use-debounce';

import { type PublicClip } from '@air/api';
import { type Clip } from '@air/api/types';

const ASSET_NAVIGATION_HOTKEY_DEBOUNCE_MS = 100;

/**
 * Area-agnostic navigation callback slot. Each area wrapper passes its own area's
 * `goToAssetPage` (see rules/area-isolation.md) — the base hook stays dumb and never
 * imports an area's navigation hook directly.
 */
export type AssetNavigationGoToAssetPage = (props: {
  asset: Clip | PublicClip;
  isAssetModalAlreadyOpen?: boolean;
}) => void;

/**
 * Base hook to handle left/right arrow key navigation for assets.
 * This is separate from the navigation arrows component so it works even when
 * the header is hidden (e.g., in fullscreen mode).
 */
export const useAssetNavigationHotkeys = ({
  goToAssetPage,
  loadMore,
  nextAsset,
  previousAsset,
  isNearEnd,
  enabled = true,
  onNavigateToAsset,
}: {
  goToAssetPage: AssetNavigationGoToAssetPage;
  loadMore: () => void;
  nextAsset: Clip | PublicClip | undefined;
  previousAsset: Clip | PublicClip | undefined;
  isNearEnd: boolean;
  enabled?: boolean;
  onNavigateToAsset?: (navigate: () => void) => void;
}) => {
  const onPreviousClick = useCallback(() => {
    if (previousAsset) {
      const navigate = () => {
        goToAssetPage({
          asset: previousAsset,
          isAssetModalAlreadyOpen: true,
        });
      };

      if (onNavigateToAsset) {
        onNavigateToAsset(navigate);
      } else {
        navigate();
      }
    }
  }, [goToAssetPage, onNavigateToAsset, previousAsset]);

  const onNextClick = useCallback(() => {
    if (nextAsset) {
      const navigate = () => {
        goToAssetPage({
          asset: nextAsset,
          isAssetModalAlreadyOpen: true,
        });
        if (isNearEnd) {
          loadMore();
        }
      };

      if (onNavigateToAsset) {
        onNavigateToAsset(navigate);
      } else {
        navigate();
      }
    }
  }, [goToAssetPage, isNearEnd, loadMore, nextAsset, onNavigateToAsset]);

  // Debounce the hotkeys to prevent rapid navigation
  const [delayedOnPrevious] = useDebouncedCallback(onPreviousClick, ASSET_NAVIGATION_HOTKEY_DEBOUNCE_MS);
  const [delayedOnNext] = useDebouncedCallback(onNextClick, ASSET_NAVIGATION_HOTKEY_DEBOUNCE_MS);

  useHotkeys(
    'left,right',
    (_keyboardEvent, hotkeysEvent) => {
      if (hotkeysEvent.keys?.includes('left')) {
        delayedOnPrevious();
      } else if (hotkeysEvent.keys?.includes('right')) {
        delayedOnNext();
      }
    },
    {
      enabled,
    },
    [delayedOnNext, delayedOnPrevious, enabled, !!nextAsset, onNavigateToAsset, !!previousAsset],
  );

  return {
    onPreviousClick,
    onNextClick,
    hasNext: !!nextAsset,
    hasPrevious: !!previousAsset,
  };
};
